import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Children, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { CSSTransition } from 'react-transition-group';
import { ThemeContext, ThemeProvider } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '../../../../../theme/Media';
import { palette } from '../../../../Palette';
import { ITEMS_LIST_PADDING_VALUE } from '../../constants';
import { usePuzzleNavigationContext } from '../../context';
import { usePathMatch } from '../../hooks';
import { CornerBadge } from '../CornerBadge';
import { ANIMATION_CLICK, ANIMATION_HOVER, ANIMATION_STATE } from './constants';
import { HeaderWrapper } from './HeaderWrapper';
import { IconAngle, ItemElement, ItemHeader, MotionDiv, MotionDivWrapper, SubList, SubListTitle, Wrapper } from './styled';
const calculateTranslateListY = (itemWrapperTop, itemListHeight) => {
    const elTransform = itemListHeight - (window.innerHeight - itemWrapperTop - 2);
    return Math.max(elTransform, 0);
};
const Item = ({ children, title, icon, itemKey, path, dataTestId, theme, label, standalone, isNew }) => {
    const match = usePathMatch({ path });
    const isDesktop = useMediaQuery({ query: Media.lgMin });
    const { activeKey, setActiveKey, isCollapsedNav } = usePuzzleNavigationContext();
    const currentTheme = useContext(ThemeContext);
    const effectiveTheme = theme ?? currentTheme ?? palette.primary;
    const itemWrapper = useRef(null);
    const itemList = useRef(null);
    const itemsWrapper = useRef(null);
    const isOpened = useMemo(() => activeKey === itemKey, [activeKey, itemKey]);
    const [coords, setCoords] = useState(undefined);
    const handleOpenMenu = useCallbackRef(() => setActiveKey(itemKey));
    const handleCloseMenu = useCallbackRef(() => setActiveKey(null));
    const handleToggleMenu = useCallbackRef(() => setActiveKey((v) => (v === itemKey ? null : itemKey)));
    const handleAnimationUpdate = useCallbackRef(() => {
        itemsWrapper.current?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
    });
    useEffect(() => {
        if (isCollapsedNav) {
            setActiveKey(null);
        }
    }, [isCollapsedNav, setActiveKey]);
    useEffect(() => {
        if (activeKey) {
            const top = itemWrapper.current?.getBoundingClientRect().top ?? 0;
            const left = itemWrapper.current?.getBoundingClientRect().right ?? 0;
            const itemListHeight = itemList.current?.getBoundingClientRect().height ?? 0;
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCoords({
                $top: top - calculateTranslateListY(top, itemListHeight),
                $left: left - ITEMS_LIST_PADDING_VALUE,
            });
        }
    }, [activeKey]);
    return (_jsx(ThemeProvider, { theme: effectiveTheme, children: _jsxs(Wrapper, { ref: itemWrapper, onMouseEnter: isCollapsedNav ? handleOpenMenu : undefined, onMouseLeave: isCollapsedNav ? handleCloseMenu : undefined, "data-testid": dataTestId, children: [_jsx(ItemElement, { "$standalone": standalone, "$isCollapsed": isCollapsedNav, "$isOpened": isOpened, children: _jsxs(ItemHeader, { "$collapsed": isCollapsedNav, onClick: !isCollapsedNav ? handleToggleMenu : undefined, children: [isNew && _jsx(CornerBadge, { variant: 'new' }), _jsx(HeaderWrapper, { match: match, icon: icon, title: title, label: label }), !isCollapsedNav && _jsx(IconAngle, { align: isOpened ? 'top' : 'bottom' })] }) }), _jsx(CSSTransition, { in: isOpened, timeout: 150, unmountOnExit: isCollapsedNav, children: _jsx(_Fragment, { children: isCollapsedNav && isDesktop ? (ReactDOM.createPortal(_jsx(MotionDiv, { animate: isOpened ? ANIMATION_STATE.OPEN : ANIMATION_STATE.CLOSED, variants: ANIMATION_HOVER, coords: coords, initial: ANIMATION_STATE.CLOSED, children: _jsxs(MotionDivWrapper, { ref: itemList, "$isCollapsedNav": true, children: [_jsx(SubListTitle, { children: title }), _jsx(SubList, { children: Children.map(children, (child) => child !== null && child) })] }) }), document.body)) : (_jsx(motion.div, { animate: isOpened ? ANIMATION_STATE.OPEN : ANIMATION_STATE.CLOSED, variants: ANIMATION_CLICK, onUpdate: handleAnimationUpdate, initial: ANIMATION_STATE.CLOSED, children: _jsx(MotionDivWrapper, { "$standalone": standalone, ref: itemsWrapper, children: Children.map(children, (child) => child !== null && child) }) })) }) })] }) }));
};
export default memo(Item);
